// src/components/demo/DemoOverlay.jsx
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDemo, DEMO_STEPS } from '../../context/DemoContext';

const PAD = 8; // breathing room around the highlighted element

// Pick the first VISIBLE match (some anchors are duplicated for mobile/desktop;
// the hidden one has a zero-size rect).
function findVisible(selector) {
  const els = Array.from(document.querySelectorAll(selector));
  return (
    els.find((el) => {
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    }) || els[0] || null
  );
}

export default function DemoOverlay() {
  const { currentStep, totalSteps, endDemo, advance, goBack } = useDemo();
  const step = DEMO_STEPS[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === totalSteps - 1;
  const isClick = step?.action === 'click';

  const [rect, setRect] = useState(null);
  const cardRef = useRef(null);
  const [card, setCard] = useState({ w: 360, h: 170 });

  // Locate the target, keep its rect in sync, and (for click steps) advance on real click.
  useEffect(() => {
    setRect(null);
    if (!step?.selector) return;

    let el = null;
    let advanced = false;
    let tries = 0;
    let locateTimer;
    const measure = () => {
      if (el) setRect(el.getBoundingClientRect());
    };
    const onClick = () => {
      if (advanced) return;
      advanced = true;
      // Let the element's native handler (e.g. navigation) run, then advance.
      setTimeout(() => advance(), 0);
    };

    const locate = () => {
      el = findVisible(step.selector);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (isClick) el.addEventListener('click', onClick);
        setTimeout(measure, 300); // after smooth scroll settles
      } else if (tries < 20) {
        tries++;
        locateTimer = setTimeout(locate, 150);
      }
    };
    locate();

    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    const interval = setInterval(measure, 300); // catch async layout shifts

    return () => {
      clearTimeout(locateTimer);
      clearInterval(interval);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
      if (el && isClick) el.removeEventListener('click', onClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step?.selector, step?.page, step?.action, advance]);

  // Measure the tooltip card so we can place it accurately.
  useLayoutEffect(() => {
    if (cardRef.current) {
      const r = cardRef.current.getBoundingClientRect();
      setCard({ w: r.width, h: r.height });
    }
  }, [currentStep, rect]);

  // ----- positioning -----
  const gap = 16;
  let cardStyle;
  if (rect) {
    const spaceBelow = window.innerHeight - rect.bottom;
    const below = spaceBelow > card.h + gap + 12;
    const top = below
      ? rect.bottom + gap
      : Math.max(12, rect.top - card.h - gap);
    let left = rect.left + rect.width / 2 - card.w / 2;
    left = Math.max(16, Math.min(left, window.innerWidth - card.w - 16));
    cardStyle = { top, left };
  } else {
    cardStyle = {
      top: window.innerHeight / 2 - card.h / 2,
      left: window.innerWidth / 2 - card.w / 2,
    };
  }

  const dim = 'rgba(0,0,0,0.6)';
  const ringStyle = rect
    ? {
        position: 'fixed',
        top: rect.top - PAD,
        left: rect.left - PAD,
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
        borderRadius: 12,
        boxShadow: `0 0 0 9999px ${dim}`,
        border: '3px solid #0CCE6B',
        zIndex: 10001,
        pointerEvents: 'none',
        animation: 'demoPulse 1.8s ease-in-out infinite',
      }
    : null;

  // Click-blocking layer.
  // - info step: one full-screen blocker absorbs ALL clicks (the page is "paused").
  // - click step: four blockers frame the target, leaving a hole so ONLY it is clickable.
  const blockers = [];
  if (isClick && rect) {
    const t = rect.top - PAD;
    const b = rect.bottom + PAD;
    const l = rect.left - PAD;
    const r = rect.right + PAD;
    const base = { position: 'fixed', zIndex: 10000, background: 'transparent' };
    blockers.push(
      { ...base, top: 0, left: 0, width: '100%', height: Math.max(0, t) },
      { ...base, top: b, left: 0, width: '100%', height: `calc(100% - ${b}px)` },
      { ...base, top: t, left: 0, width: Math.max(0, l), height: b - t },
      { ...base, top: t, left: r, width: `calc(100% - ${r}px)`, height: b - t }
    );
  }

  return (
    <>
      <style>{`
        @keyframes demoPulse {
          0%, 100% { outline: 0 solid rgba(12,206,107,0.55); outline-offset: 0; }
          50%      { outline: 7px solid rgba(12,206,107,0); outline-offset: 0; }
        }
      `}</style>

      {/* Dimmer when there's no target yet (centered steps / while locating) */}
      {!rect && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9998,
            background: dim,
            pointerEvents: isClick ? 'none' : 'auto',
          }}
        />
      )}

      {/* Spotlight ring (also provides the dim via its box-shadow) */}
      {ringStyle && <div style={ringStyle} />}

      {/* Click-blocking */}
      {isClick
        ? blockers.map((s, i) => <div key={i} style={s} />)
        : rect && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 10000,
                background: 'transparent',
                pointerEvents: 'auto',
              }}
            />
          )}

      {/* On-site tooltip card */}
      <div
        ref={cardRef}
        style={{
          position: 'fixed',
          ...cardStyle,
          width: 360,
          maxWidth: '90vw',
          zIndex: 10002,
          pointerEvents: 'auto',
        }}
        className="bg-white rounded-xl shadow-2xl border border-neutral-200 overflow-hidden animate-fade-in"
      >
        <div className="px-5 pt-4 pb-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-black uppercase tracking-widest text-[#0CCE6B]">
              Demo
            </span>
            <span className="text-xs font-semibold text-neutral-400">
              {currentStep + 1} / {totalSteps}
            </span>
          </div>

          {/* Message */}
          <p className="text-[15px] leading-snug font-medium text-neutral-800">
            {step?.message}
          </p>

          {/* Footer */}
          <div className="mt-4 flex items-center justify-between gap-3">
            <button
              onClick={endDemo}
              className="text-xs text-neutral-400 hover:text-neutral-700 transition-colors"
            >
              Skip tour
            </button>

            <div className="flex items-center gap-2">
              {!isFirst && (
                <button
                  onClick={goBack}
                  className="px-3 py-1.5 text-sm font-semibold rounded-md bg-neutral-100 hover:bg-neutral-200 text-neutral-700 transition-colors"
                >
                  Back
                </button>
              )}

              {isClick ? (
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0CCE6B]">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-[#0CCE6B] opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#0CCE6B]" />
                  </span>
                  Click the highlighted button
                </span>
              ) : isLast ? (
                <button
                  onClick={endDemo}
                  className="px-4 py-1.5 text-sm font-bold rounded-md bg-gradient-to-r from-[#474747] to-[#0CCE6B] text-white hover:opacity-90 transition-opacity"
                >
                  Finish
                </button>
              ) : (
                <button
                  onClick={advance}
                  className="px-4 py-1.5 text-sm font-bold rounded-md bg-gradient-to-r from-[#474747] to-[#0CCE6B] text-white hover:opacity-90 transition-opacity"
                >
                  Continue →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
